import { Box, Button, Grid } from '@itandi/itandi-bb-ui';
import { toast } from 'react-toastify';
import { useCallback, useState } from 'react';
import SettingStyles from '../SettingStyle.module.scss';
import { useVendorNotifications } from '~/hooks/vendors/useVendorNotifications';
import { useStatuses } from '~/hooks/vendors/useStatuses';
import { NotificationList } from '~/components/common/notifications/settings/NotificationList';
import type { Notification, NotificationEdit } from '~/models/Notification';
import type { NskResponse } from '~/models/Api';
import {
  create,
  destroy,
  update
} from '~/utils/api/vendors/VendorNotification';
import { useSessions } from '~/hooks/vendors/useSessions';
import type { Status } from '~/models/Status';
import { getWithVendorStatuses } from '~/models/Status';
import { useLineUserAccounts } from '~/hooks/vendors/useLineUserAccounts';
import { NotificationModal } from '~/components/common/notifications/settings/NotificationModal';
import { isSuccess } from '~/utils/api/api_handler';

export const NotificationSetting: React.FC = () => {
  const { data: notifications, mutate: mutateNotifications } =
    useVendorNotifications().index();
  const { data: statuses } = useStatuses().index();
  const { data: session } = useSessions().index();
  const { data: lineUserAccounts } = useLineUserAccounts().index();
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);
  const handleCreate = useCallback(
    async (notification: NotificationEdit): Promise<NskResponse<void>> =>
      create(notification),
    []
  );
  const handleUpdate = useCallback(
    async (
      notification: NotificationEdit,
      notificationId: number
    ): Promise<NskResponse<void>> => update(notification, notificationId),
    []
  );
  const handleDestroy = useCallback(
    async (notification: Notification): Promise<NskResponse<void>> =>
      destroy(notification),
    []
  );
  const handleMutate = useCallback(async () => {
    await mutateNotifications?.();
  }, [mutateNotifications]);
  const handleAcceptSubmit = useCallback(
    async (notification: NotificationEdit): Promise<void> => {
      const response = await handleCreate(notification);
      if (isSuccess(response)) {
        await handleMutate();
        setIsNotificationModalOpen(false);
        toast.success('通知設定を追加しました。');
        return;
      }
      toast.error('通知設定の追加に失敗しました。');
    },
    [handleMutate, handleCreate]
  );
  if (session === null || notifications === null || statuses === null) {
    return null;
  }
  const displayStatuses: Status[] = getWithVendorStatuses(statuses);
  return (
    <>
      <Grid>
        <Box>
          <h3 className={SettingStyles.SettingTitle}>通知設定</h3>
        </Box>
        <Box>
          <NotificationList
            handleDestroy={handleDestroy}
            handleMutate={handleMutate}
            handleUpdate={handleUpdate}
            lineUserAccounts={lineUserAccounts?.lineUserAccounts}
            notifications={notifications}
            statuses={displayStatuses}
            type="VendorNotification"
            useLineNotification={
              session.vendorCompany.useLineNotification || false
            }
          />
        </Box>
        <Box marginTop={8}>
          <Button
            onClick={() => setIsNotificationModalOpen(true)}
            variant="secondary"
          >
            通知設定の追加
          </Button>
        </Box>
      </Grid>
      {isNotificationModalOpen && (
        <NotificationModal
          handleAcceptSubmit={handleAcceptSubmit}
          handleClose={() => setIsNotificationModalOpen(false)}
          lineUserAccounts={lineUserAccounts?.lineUserAccounts || null}
          statuses={displayStatuses}
          subtypes={null}
          type="VendorNotification"
          useLineNotification={
            session.vendorCompany.useLineNotification || false
          }
        />
      )}
    </>
  );
};
